import { env } from 'src/config/env';
import { type LocalStorageKey } from '../config/constants';
import { createStore, useStore } from './createStore';

const appStorageKey = `baobab_${env.mode}`;

type StorageScope = 'local' | 'session';

type ScopeState = {
  memory: Map<string, string>;
  refused: Set<string>;
};

const scopes: Record<StorageScope, ScopeState> = {
  local: { memory: new Map(), refused: new Set() },
  session: { memory: new Map(), refused: new Set() },
};

const memoryStore = scopes.local.memory;
const keysLocalStorageRefused = scopes.local.refused;
const writeCount = createStore(0);

export const SESSION_SCOPE = { scope: 'session' } as const;

type StoredItemOptions = { isMemoryOnly?: boolean; scope?: StorageScope };

const backendOf = (scope: StorageScope): Storage =>
  scope === 'session' ? sessionStorage : localStorage;

export const readStoredItem = (
  key: string,
  { isMemoryOnly = false, scope = 'local' }: StoredItemOptions = {},
): string | null => {
  const { memory, refused } = scopes[scope];
  if (isMemoryOnly || refused.has(key)) {
    return memory.get(key) ?? null;
  }
  try {
    return backendOf(scope).getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
};

export const writeStoredItem = (
  key: string,
  value: string | null,
  { isMemoryOnly = false, scope = 'local' }: StoredItemOptions = {},
): boolean => {
  const { memory, refused } = scopes[scope];
  let isPersisted = false;
  if (!isMemoryOnly) {
    try {
      const backend = backendOf(scope);
      if (value === null) backend.removeItem(key);
      else backend.setItem(key, value);
      refused.delete(key);
      isPersisted = true;
    } catch {
      refused.add(key);
    }
  }
  if (value === null || isPersisted) memory.delete(key);
  else memory.set(key, value);
  if (scope === 'local') writeCount.setState(count => count + 1);
  return isPersisted;
};

export const removeStoredItem = (
  key: string,
  options?: StoredItemOptions,
): boolean => writeStoredItem(key, null, options);

const persistedKeysWithPrefix = (scope: StorageScope, prefix: string): string[] => {
  try {
    const backend = backendOf(scope);
    return Array.from({ length: backend.length }, (_, index) => backend.key(index)).filter(
      (key): key is string => key !== null && key.startsWith(prefix),
    );
  } catch {
    return [];
  }
};

export const removeStoredItemsByPrefix = (
  prefix: string,
  { scope = 'local' }: Pick<StoredItemOptions, 'scope'> = {},
) => {
  const memoryKeys = [...scopes[scope].memory.keys()].filter(key =>
    key.startsWith(prefix),
  );
  new Set([...memoryKeys, ...persistedKeysWithPrefix(scope, prefix)]).forEach(key =>
    removeStoredItem(key, { scope }),
  );
};

export const subscribeStoredItems = (listener: () => void) => {
  const unsubscribe = writeCount.subscribe(listener);
  window.addEventListener('storage', listener);
  return () => {
    unsubscribe();
    window.removeEventListener('storage', listener);
  };
};

export const resetStoredItemsForTests = () => {
  Object.values(scopes).forEach(({ memory, refused }) => {
    memory.clear();
    refused.clear();
  });
};

const parseBlob = (raw: string | null | undefined): ReturnType<typeof JSON.parse> => {
  try {
    const blob = JSON.parse(raw ?? '{}');
    return typeof blob === 'object' && blob !== null && !Array.isArray(blob) ? blob : {};
  } catch {
    return {};
  }
};

const readBlob = (): { blob: ReturnType<typeof JSON.parse>; isPersistedReadable: boolean } => {
  const memoryRaw = memoryStore.get(appStorageKey);
  if (keysLocalStorageRefused.has(appStorageKey)) {
    return { blob: parseBlob(memoryRaw), isPersistedReadable: true };
  }
  let persistedRaw: string | null;
  try {
    persistedRaw = localStorage.getItem(appStorageKey);
  } catch {
    return { blob: parseBlob(memoryRaw), isPersistedReadable: false };
  }
  const persisted = parseBlob(persistedRaw);
  return {
    blob: memoryRaw === undefined ? persisted : { ...persisted, ...parseBlob(memoryRaw) },
    isPersistedReadable: true,
  };
};

export const getInLocalStorage = () => readBlob().blob;

export const getInLocalStorageByKey = (key: LocalStorageKey) =>
  getInLocalStorage()[key];

export const setInLocalStorage = (key?: LocalStorageKey, value?: unknown) => {
  const { blob: currentLocalStorage, isPersistedReadable } = readBlob();
  if (key) currentLocalStorage[key] = value;

  writeStoredItem(appStorageKey, JSON.stringify(currentLocalStorage), {
    isMemoryOnly: !isPersistedReadable,
  });
};

export const useStoredItemsVersion = () =>
  useStore(writeCount);
